import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { learningCenter } from '../data/mock';
import { ArrowRight, Download, FileText, BarChart2, BookOpen, TrendingUp } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollAnimation';

const iconMap = {
  'corporate-brochure': FileText,
  'complete-solution-guide': BookOpen,
  'case-studies': BarChart2,
  'white-papers': FileText,
};

const LearningPage = () => {
  const location = useLocation();
  const resourceId = location.pathname.replace('/', '');
  const resource = learningCenter[resourceId];

  const [mainRef, mainVisible] = useScrollReveal({ threshold: 0.1 });
  const [itemsRef, itemsVisible] = useScrollReveal({ threshold: 0.1 });

  const Icon = iconMap[resourceId] || FileText;

  if (!resource) {
    return (
      <div>
        <PageHeader title="Resource Not Found" breadcrumbs={[{ label: 'Learning Center' }]} />
        <div className="py-20 text-center">
          <p className="text-muted-foreground mb-4">The resource you're looking for doesn't exist.</p>
          <Link to="/" style={{ color: '#43a047' }} className="text-sm hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <PageHeader
        title={resource.title}
        breadcrumbs={[
          { label: 'Learning Center', href: '/learning-center' },
          { label: resource.title },
        ]}
      />

      <div className="py-14 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* ── Main ── */}
            <div className="flex-1" ref={mainRef}>
              <div className={`${mainVisible ? 'anim-slide-right' : 'reveal-hidden'}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: '#43a047' }}
                  >
                    <Icon size={26} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: '#43a047' }}>Learning Center</p>
                    <h2 className="text-3xl font-extrabold text-foreground">{resource.title}</h2>
                  </div>
                </div>
                <div className="w-14 h-1 rounded-full mb-5" style={{ background: '#43a047' }} />
                <p className="text-muted-foreground leading-relaxed mb-4">{resource.description}</p>
                {resource.content && (
                  <p className="text-muted-foreground leading-relaxed mb-6">{resource.content}</p>
                )}
              </div>

              {/* Case Studies */}
              {resource.items && resourceId === 'case-studies' && (
                <div ref={itemsRef}>
                  <h3 className={`text-lg font-bold text-foreground mb-5 ${itemsVisible ? 'anim-fade-up' : 'reveal-hidden'}`}>
                    Featured Case Studies
                  </h3>
                  <div className="space-y-4">
                    {resource.items.map((cs, i) => (
                      <div
                        key={i}
                        className={`card-hover border border-border rounded-xl p-5 bg-card ${itemsVisible ? 'anim-fade-up' : 'reveal-hidden'}`}
                        style={{ animationDelay: `${i * 0.1}s` }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f5e9' }}>
                              <TrendingUp size={18} style={{ color: '#43a047' }} />
                            </div>
                            <div>
                              <h4 className="font-bold text-foreground mb-0.5">{cs.company}</h4>
                              <p className="text-xs text-muted-foreground">Service: {cs.service}</p>
                            </div>
                          </div>
                          <span
                            className="text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0"
                            style={{ backgroundColor: '#43a047' }}
                          >
                            {cs.result}
                          </span>
                        </div>
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-1 text-xs font-semibold mt-3 group/link"
                          style={{ color: '#43a047' }}
                        >
                          Read Full Case Study
                          <ArrowRight size={12} className="group-hover/link:translate-x-1" style={{ transition: 'transform 0.2s' }} />
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* White Papers */}
              {resource.items && resourceId === 'white-papers' && (
                <div ref={itemsRef}>
                  <h3 className={`text-lg font-bold text-foreground mb-5 ${itemsVisible ? 'anim-fade-up' : 'reveal-hidden'}`}>
                    Available White Papers
                  </h3>
                  <div className="space-y-3">
                    {resource.items.map((paper, i) => (
                      <div
                        key={i}
                        className={`card-hover flex items-center gap-3 border border-border rounded-xl p-4 bg-card ${itemsVisible ? 'anim-fade-up' : 'reveal-hidden'}`}
                        style={{ animationDelay: `${i * 0.09}s` }}
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f5e9' }}>
                          <FileText size={16} style={{ color: '#43a047' }} />
                        </div>
                        <span className="text-sm text-foreground flex-1">{paper}</span>
                        <button
                          className="flex items-center gap-1 text-xs font-semibold flex-shrink-0 px-3 py-1.5 rounded-full"
                          style={{ color: '#43a047', border: '1px solid #43a047', transition: 'background 0.2s, color 0.2s' }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#43a047'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#43a047'; }}
                        >
                          <Download size={11} /> Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Download CTA */}
              {(resourceId === 'corporate-brochure' || resourceId === 'complete-solution-guide') && (
                <div
                  className={`rounded-2xl p-7 mt-6 border border-border bg-muted ${mainVisible ? 'anim-fade-up delay-300' : 'reveal-hidden'}`}
                >
                  <h3 className="text-lg font-bold text-foreground mb-2">Download Now</h3>
                  <p className="text-sm text-muted-foreground mb-5">Fill out the form to receive your free copy directly to your inbox.</p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-white text-sm font-semibold px-7 py-3 rounded-full shadow-sm"
                    style={{ backgroundColor: '#43a047', transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <Download size={14} />
                    Request Your Copy
                  </Link>
                </div>
              )}
            </div>

            {/* ── Sidebar ── */}
            <div className="lg:w-72 space-y-5">
              <div className="bg-muted border border-border rounded-xl p-5">
                <h4 className="font-bold text-foreground mb-4 text-xs uppercase tracking-wider">Learning Center</h4>
                <ul className="space-y-1">
                  {Object.entries(learningCenter).map(([key, r]) => (
                    <li key={key}>
                      <Link
                        to={`/${key}`}
                        className={`flex items-center gap-2 text-sm py-2 px-3 rounded-lg ${
                          key === resourceId ? 'text-white font-semibold' : 'text-muted-foreground hover:text-[#43a047] hover:bg-muted'
                        }`}
                        style={{
                          backgroundColor: key === resourceId ? '#43a047' : 'transparent',
                          transition: 'all 0.2s',
                        }}
                      >
                        <ArrowRight size={12} />
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl p-5 text-white relative overflow-hidden" style={{ backgroundColor: '#43a047' }}>
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10" />
                <h4 className="font-bold mb-2 text-sm relative">Ready to Get Started?</h4>
                <p className="text-xs text-green-100 leading-relaxed mb-4 relative">
                  Contact our team today to learn how B2BTechLeads can help your business grow.
                </p>
                <Link
                  to="/contact"
                  className="relative flex items-center justify-center gap-2 text-sm font-semibold bg-white text-[#43a047] px-4 py-2 rounded-full"
                  style={{ transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                >
                  Contact Us <ArrowRight size={13} />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPage;
